import { Divider, IconButton, Stack, Typography } from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { useCallback, useState } from 'react';

import { OwnBucketSuggestedPolicyDialogBody } from '@/app/(dashboard)/settings/vaults/ownBucketSuggestedPolicyDialogBody';
import { AccountIndicator } from '@/components/accounts/accountIndicator';
import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { CopyToClipboardButton } from '@/components/buttons/copyToClipboardButton';
import { OverflowTooltip } from '@/components/tooltips/overflowTooltip';
import { useDAL } from '@/contexts/useDAL';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { useRoles } from '@/contexts/useRoles';
import { useWorkspace } from '@/contexts/useWorkspace';
import { CloudProviders } from '@/data/inventory/data';
import type { BackupVault } from '@/data/vaults/backupVault';

import { VaultColorSelectorWithButtons } from './createBackupVaultPanel';
import { GcpKmsCopyablePolicy } from './gcpKmsCopyablePolicy';
import { KmsSuggestedPolicyContent } from './kmsSuggestedPolicy';
import { useBackupVaultColorUpdate } from './useBackupVaultColorUpdate';
import { VaultColoredDot } from './vaultColoredDot';

import { RenameEntityName } from '../wizard/renameEntityName';

interface UseVaultDetailsDialogOptions {
  editable: boolean;
  onSaveSuccess?: () => void;
}

export function useVaultDetailsDialog(isLoading = false) {
  const { sharedDialog } = useWorkspace();

  const open = useCallback(
    (vault: BackupVault, options: UseVaultDetailsDialogOptions) => {
      const openKmsSuggestedPolicy = () => {
        sharedDialog.open(
          'vault-details-kms-policy',
          <KmsSuggestedPolicyContent
            kmsKey={vault.vaultAttributes.aws?.encryptionKey}
            cloudProvider={vault.vaultAttributes.cloudProvider}
          />,
          {
            isLoading,
            header: {
              title: 'Get suggested policy',
              subtitle:
                'To restore from your self-managed vault, apply this policy in your AWS account. Without it, restoring can fail.',
            },
          }
        );
      };

      const openGcpKmsSuggestedPolicy = () => {
        sharedDialog.open(
          'vault-details-gcp-kms-policy',
          <GcpKmsSuggestedPolicyContent backupVault={vault} />,
          {
            isLoading,
            header: {
              title: 'Get suggested permissions',
              subtitle:
                'Run these commands to grant the required service accounts access to your Cloud KMS key. Without these permissions, vault operations may fail.',
            },
          }
        );
      };

      const openOwnBucketSuggestedPolicy = () => {
        sharedDialog.open(
          'vault-details-own-bucket-policy',
          <OwnBucketSuggestedPolicyDialogBody backupVault={vault} />,
          {
            isLoading,
            header: {
              title: 'Get suggested policy',
              subtitle:
                'To restore from your self-managed vault, apply this policy in your AWS account. Without it, restoring can fail.',
            },
          }
        );
      };

      sharedDialog.open(
        'vault-details',
        <VaultDetailsContent
          vault={vault}
          vaultAccountId={vault.vaultAccountId}
          editable={options.editable}
          onSaveSuccess={options.onSaveSuccess ?? (() => {})}
          onOpenKmsSuggestedPolicy={openKmsSuggestedPolicy}
          onOpenGcpKmsSuggestedPolicy={openGcpKmsSuggestedPolicy}
          onOpenOwnBucketSuggestedPolicy={openOwnBucketSuggestedPolicy}
        />,
        {
          isLoading,
          header: {
            title: 'Vault Details',
            subtitle:
              "View the vault's configuration. Only the name and color can be edited.",
          },
        }
      );
    },
    [sharedDialog, isLoading]
  );

  return { open };
}

const VaultDetailsContent = ({
  vault: initialVault,
  vaultAccountId,
  editable,
  onSaveSuccess,
  onOpenKmsSuggestedPolicy,
  onOpenGcpKmsSuggestedPolicy,
  onOpenOwnBucketSuggestedPolicy,
}: {
  vault: BackupVault;
  vaultAccountId?: string;
  editable: boolean;
  onSaveSuccess: () => void;
  onOpenKmsSuggestedPolicy: () => void;
  onOpenGcpKmsSuggestedPolicy: () => void;
  onOpenOwnBucketSuggestedPolicy: () => void;
}) => {
  const [vault, setVault] = useState(initialVault);
  const [isEditingColor, setIsEditingColor] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const { isAuthorized } = useRoles();
  const isEditable = editable && isAuthorized('update:vaults');
  const dal = useDAL();
  const { updateVaultColor } = useBackupVaultColorUpdate();
  const { byovEnabled = false, EnableMrapForByov: enableMrapForByov = false } =
    useFeatureFlags();
  const enableMrap = byovEnabled && enableMrapForByov;

  const vaultAccounts = dal.cloudAccounts.vault.useList();
  const vaultAccount = vaultAccounts.data?.accounts.find(
    (account) => account.id === vaultAccountId
  );

  const accountLabel =
    vaultAccount?.cloudProvider === Provider.Azure
      ? 'Subscription'
      : vaultAccount?.cloudProvider === Provider.Gcp
        ? 'Project'
        : 'Account';

  return (
    <Stack
      marginBottom='16px'
      gap='16px'
      divider={<Divider sx={{ borderStyle: 'dashed' }} />}
    >
      <RowWrapper>
        <Typography width='150px'>Name</Typography>
        {isEditable ? (
          <RenameEntityName
            name={vault.name}
            onSubmit={(name) => {
              const updatedVault = { ...vault, name };
              void dal.vaults.update(updatedVault).then(() => {
                setVault(updatedVault);
                setIsEditingName(false);
                onSaveSuccess();
              });
            }}
            bold={false}
            editMode={isEditingName}
            setEditMode={setIsEditingName}
          />
        ) : (
          <Typography>{vault.name}</Typography>
        )}
      </RowWrapper>
      <RowWrapper>
        <Typography width='150px'>ID</Typography>
        <Stack direction='row' gap='16px' alignItems='center'>
          <Typography>{vault.id}</Typography>
          <CopyToClipboardButton textToCopy={vault.id} />
        </Stack>
      </RowWrapper>
      <RowWrapper>
        <Typography width='150px'>Color</Typography>
        {isEditingColor && isEditable ? (
          <VaultColorSelectorWithButtons
            initialData={vault}
            saveData={(vault) => {
              void updateVaultColor(vault).then(() => {
                setVault(vault);
                setIsEditingColor(false);
                onSaveSuccess();
              });
            }}
            onCancel={() => setIsEditingColor(false)}
            disabled={false}
          />
        ) : (
          <Stack alignItems='center' direction='row' gap='12px'>
            <VaultColoredDot color={vault.backgroundColor} />
            {isEditable && (
              <IconButton
                onClick={() => setIsEditingColor(true)}
                data-testid='rename-entity-name-edit-btn'
              >
                <i
                  className={
                    'material-symbols-stylus-outline w-[20px] h-[20px] text-(--mui-palette-text-primary)'
                  }
                />
              </IconButton>
            )}
          </Stack>
        )}
      </RowWrapper>
      <RowWrapper>
        <Typography width='150px'>Cloud</Typography>
        <Typography>
          {CloudProviders[vault.vaultAttributes.cloudProvider].displayName}
        </Typography>
      </RowWrapper>
      <RowWrapper>
        <Typography width='150px'>Region</Typography>
        <Typography>{vault.region}</Typography>
      </RowWrapper>
      <RowWrapper>
        <Typography width='150px'>Type</Typography>
        <Typography>
          {vaultAccount && !vaultAccount.isCustomerOwned
            ? 'Eon-managed'
            : 'Self-managed'}
        </Typography>
      </RowWrapper>
      <RowWrapper>
        <Typography width='150px'>{accountLabel}</Typography>
        {vaultAccount ? <AccountIndicator account={vaultAccount} /> : '--'}
      </RowWrapper>
      {vaultAccount?.isCustomerOwned &&
        vault.vaultAttributes.aws?.bucketName && (
          <RowWrapper justifyContent='space-between'>
            <Stack direction='row' gap='16px'>
              <Typography width='150px'>Bucket</Typography>
              <Typography>{vault.vaultAttributes.aws?.bucketName}</Typography>
            </Stack>
            <ButtonWithIcon
              variant='text'
              text='Get Suggested Policy'
              icon='material-symbols-data-object'
              onClick={onOpenOwnBucketSuggestedPolicy}
            />
          </RowWrapper>
        )}
      {enableMrap &&
        vaultAccount?.isCustomerOwned &&
        vault.vaultAttributes.aws?.mrapAlias && (
          <RowWrapper>
            <Stack direction='row' gap='16px'>
              <Typography width='150px'>MRAP alias</Typography>
              <Typography>{vault.vaultAttributes.aws.mrapAlias}</Typography>
            </Stack>
          </RowWrapper>
        )}
      {vaultAccount?.isCustomerOwned &&
        vault.vaultAttributes.gcp?.bucketName && (
          <RowWrapper>
            <Stack direction='row' gap='16px'>
              <Typography width='150px'>Bucket</Typography>
              <Typography>{vault.vaultAttributes.gcp?.bucketName}</Typography>
            </Stack>
          </RowWrapper>
        )}
      {vaultAccount?.isCustomerOwned &&
        vault.vaultAttributes.azure?.storageAccount && (
          <RowWrapper>
            <Stack direction='row' gap='16px'>
              <Typography width='150px'>Storage Account</Typography>
              <Typography>
                {vault.vaultAttributes.azure?.storageAccount}
              </Typography>
            </Stack>
          </RowWrapper>
        )}
      {vault.vaultAttributes.aws?.encryptionKey && (
        <RowWrapper justifyContent='space-between'>
          <Stack direction='row' gap='16px'>
            <Typography width='150px'>KMS Key</Typography>
            <Stack width='170px'>
              <OverflowTooltip
                text={vault.vaultAttributes.aws?.encryptionKey}
              />
            </Stack>
          </Stack>
          <ButtonWithIcon
            variant='text'
            text='Get Suggested Policy'
            icon='material-symbols-data-object'
            onClick={onOpenKmsSuggestedPolicy}
          />
        </RowWrapper>
      )}
      {vault.vaultAttributes.gcp?.encryptionKey && (
        <RowWrapper justifyContent='space-between'>
          <Stack direction='row' gap='16px'>
            <Typography width='150px'>Cloud KMS Key</Typography>
            <Stack width='170px'>
              <OverflowTooltip
                text={vault.vaultAttributes.gcp?.encryptionKey}
              />
            </Stack>
          </Stack>
          <ButtonWithIcon
            variant='text'
            text='Get Suggested Permissions'
            icon='material-symbols-data-object'
            onClick={onOpenGcpKmsSuggestedPolicy}
          />
        </RowWrapper>
      )}
      {vault.vaultAttributes.azure?.encryptionKey && (
        <RowWrapper>
          <Stack direction='row' gap='16px'>
            <Typography width='150px'>Key Vault key</Typography>
            <Stack width='170px'>
              <OverflowTooltip
                text={vault.vaultAttributes.azure?.encryptionKey}
              />
            </Stack>
          </Stack>
        </RowWrapper>
      )}
    </Stack>
  );
};

const GcpKmsSuggestedPolicyContent = ({
  backupVault,
}: {
  backupVault: BackupVault;
}) => {
  const dal = useDAL();
  const { data: scanningAccounts } = dal.cloudAccounts.scanning.useList();

  const gcpScanningAccount = scanningAccounts?.accounts.find(
    (a) => a.cloudProvider === Provider.Gcp
  );

  if (!gcpScanningAccount?.id) {
    return null;
  }

  return (
    <Stack paddingX='40px' gap='24px' alignItems='flex-start'>
      <GcpKmsCopyablePolicy
        backupVault={backupVault}
        scanningAccountId={gcpScanningAccount.id}
      />
    </Stack>
  );
};

const RowWrapper = ({
  children,
  justifyContent,
}: React.PropsWithChildren<{
  justifyContent?: string;
}>) => {
  return (
    <Stack
      minHeight='32px'
      flexGrow={1}
      paddingRight='24px'
      paddingLeft='40px'
      direction='row'
      gap='16px'
      alignItems='center'
      justifyContent={justifyContent}
    >
      {children}
    </Stack>
  );
};
